import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Loader2, FileText, Trash2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface AIReport {
  id: string;
  report_type: string;
  title: string;
  content: string;
  model: string | null;
  created_at: string;
}

interface AIReportTabProps {
  jobId: string;
}

const REPORT_TYPES = [
  { value: "job_summary", label: "Job Summary" },
  { value: "storm", label: "Storm Impact Report" },
  { value: "exec", label: "Executive Summary" },
  { value: "custom", label: "Custom Report" },
];

export const AIReportTab = ({ jobId }: AIReportTabProps) => {
  const [reports, setReports] = useState<AIReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState("job_summary");
  const [instructions, setInstructions] = useState("");
  const [active, setActive] = useState<AIReport | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ai_reports")
      .select("*")
      .eq("job_id", jobId)
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Failed to load reports");
    } else {
      setReports(data ?? []);
      if (data && data.length && !active) setActive(data[0]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "generate-ai-report",
        { body: { jobId, reportType, instructions } },
      );
      if (error) throw error;
      toast.success("Report generated");
      setInstructions("");
      setActive(data.report);
      load();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("ai_reports").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete");
      return;
    }
    toast.success("Report deleted");
    if (active?.id === id) setActive(null);
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 p-4">
      <div className="space-y-4">
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">Generate report</h3>
          </div>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_TYPES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Optional instructions (e.g. focus on margin risk)"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={3}
          />
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full gap-2"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Generating…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Generate
              </>
            )}
          </Button>
        </Card>

        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" /> History
          </h3>
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reports yet</p>
          ) : (
            <ul className="space-y-2">
              {reports.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setActive(r)}
                    className={`w-full text-left p-2 rounded-md border transition-colors ${
                      active?.id === r.id
                        ? "bg-muted border-primary/40"
                        : "border-transparent hover:bg-muted"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate">
                        {r.title}
                      </span>
                      <Badge variant="outline" className="text-[10px]">
                        {r.report_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Clock className="w-3 h-3" />
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="p-6 min-h-[400px]">
        {active ? (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">{active.title}</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {active.model} · {new Date(active.created_at).toLocaleString()}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(active.id)}
                className="text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown>{active.content}</ReactMarkdown>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-16">
            <Sparkles className="w-10 h-10 mb-3 text-primary/50" />
            <p className="font-medium">No report selected</p>
            <p className="text-sm">Generate a new AI report to get started.</p>
          </div>
        )}
      </Card>
    </div>
  );
};
